import type { ListRewardsParams } from "./client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { loyaltyApi } from "./client";

export const loyaltyKeys = {
  all: ["loyalty"] as const,
  rules: () => [...loyaltyKeys.all, "rules"] as const,
  ruleHistory: () => [...loyaltyKeys.all, "rules", "history"] as const,
  rewardsSummary: () => [...loyaltyKeys.all, "rewards", "summary"] as const,
  rewardsList: (params?: ListRewardsParams) =>
    [...loyaltyKeys.all, "rewards", "list", params] as const,
  rewardDetail: (rewardId: string) =>
    [...loyaltyKeys.all, "rewards", rewardId] as const,
  variantOptions: (search?: string) =>
    [...loyaltyKeys.all, "rewards", "variant-options", search] as const,
};

export function useRewardsSummaryQuery() {
  return useQuery({
    queryKey: loyaltyKeys.rewardsSummary(),
    queryFn: () => loyaltyApi.getRewardsSummary(),
  });
}

export function useRewardsQuery(params?: ListRewardsParams) {
  return useQuery({
    queryKey: loyaltyKeys.rewardsList(params),
    queryFn: () => loyaltyApi.listRewards(params),
  });
}

export function useRewardQuery(rewardId: string) {
  return useQuery({
    queryKey: loyaltyKeys.rewardDetail(rewardId),
    queryFn: () => loyaltyApi.getReward(rewardId),
    enabled: !!rewardId,
  });
}

export function useVariantOptionsQuery(search?: string) {
  return useQuery({
    queryKey: loyaltyKeys.variantOptions(search),
    queryFn: () => loyaltyApi.listVariantOptions(search, 200),
  });
}

export function useCreateRewardMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: loyaltyApi.createReward,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...loyaltyKeys.all, "rewards", "list"],
      });
      qc.invalidateQueries({ queryKey: loyaltyKeys.rewardsSummary() });
    },
  });
}

export function useUpdateRewardMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rewardId,
      payload,
    }: { rewardId: string; payload: Parameters<typeof loyaltyApi.updateReward>[1] }) =>
      loyaltyApi.updateReward(rewardId, payload),
    onSuccess: (_, { rewardId }) => {
      qc.invalidateQueries({ queryKey: loyaltyKeys.rewardDetail(rewardId) });
      qc.invalidateQueries({
        queryKey: [...loyaltyKeys.all, "rewards", "list"],
      });
      qc.invalidateQueries({ queryKey: loyaltyKeys.rewardsSummary() });
    },
  });
}

export function useDeactivateRewardMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: loyaltyApi.deactivateReward,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...loyaltyKeys.all, "rewards", "list"],
      });
      qc.invalidateQueries({ queryKey: loyaltyKeys.rewardsSummary() });
    },
  });
}
